import { ScheduledEventDto } from '@act/shared/data-transfer-objects';
import {
  ScheduledEventDeletedEventPayload,
  ScheduledEventEventPayload,
  ScheduledEventEventTypes,
} from '@act/shared/socket-events';
import {
  PayloadAction,
  createAction,
  createEntityAdapter,
  createSelector,
  createSlice,
} from '@reduxjs/toolkit';
import { scheduledEventRequests } from '../requests/scheduled-event.requests';
import { ScheduledEventState } from '../types/scheduledEventTypes';
import { createApiThunk } from '../utils/api-thunk';

import { addToEffectsMiddleware } from 'redux/utils/effects-middleware';
import { useAppSelector } from '../store-hooks';
import { getPendingItems } from './feedSlice';
import { newMembersLoaded } from './memberSlice';
import type { WebsocketEventActions } from '../types/websocket-types';
import { resetRedux } from '../actions/shared-actions';

// THUNKS/ACTIONS ==============================================================
export const getScheduledEvents = createApiThunk(
  scheduledEventRequests.getScheduledEvents
);
export const getScheduledEventsForGuide = createApiThunk(
  scheduledEventRequests.getScheduledEventsForGuide
);
export const createScheduledEventForUser = createApiThunk(
  scheduledEventRequests.createScheduledEventForUser
);
export const snoozeScheduledEventForUser = createApiThunk(
  scheduledEventRequests.snoozeScheduledEventForUser
);
export const deleteScheduledEventForUser = createApiThunk(
  scheduledEventRequests.deleteScheduledEventForUser
);
export const updateScheduledEventForUser = createApiThunk(
  scheduledEventRequests.updateScheduledEvent
);

type LoadEventsPayload = {
  userId: string;
  startEpochTime?: number;
  endEpochTime?: number;
};
export const loadScheduledEvents = createAction<LoadEventsPayload>(
  'scheduled-events/loadEvents'
);
export const loadScheduledEventsForGuide = createAction<LoadEventsPayload>(
  'scheduled-events/loadEventsForGuide'
);

export const createScheduledEventSuccess = createAction<ScheduledEventDto>(
  'scheduled-events/createScheduledEventSuccess'
);

export const updateScheduledEventSuccess = createAction<ScheduledEventDto>(
  'scheduled-events/updateScheduledEventSuccess'
);

// INITIAL STATE ===============================================================
const scheduledEventAdapter = createEntityAdapter<ScheduledEventDto>();
const initialState: ScheduledEventState = scheduledEventAdapter.getInitialState(
  {
    loadingStatus: 'initial',
    loadingGuideEventsStatus: 'initial',
  }
);

// SLICE =======================================================================
export const scheduledEventsSlice = createSlice({
  name: 'scheduledEvents',
  initialState,
  reducers: {
    scheduledEventCreated: (
      state: ScheduledEventState,
      action: PayloadAction<ScheduledEventEventPayload>
    ) => {
      scheduledEventAdapter.upsertOne(state, action.payload.scheduledEvent);
    },
    scheduledEventUpdated: (
      state: ScheduledEventState,
      action: PayloadAction<ScheduledEventEventPayload>
    ) => {
      const scheduledEvent = action.payload.scheduledEvent;
      if (scheduledEvent.resolved) {
        scheduledEventAdapter.removeOne(state, scheduledEvent.id);
      } else {
        scheduledEventAdapter.upsertOne(state, scheduledEvent);
      }
    },
    scheduledEventDeleted: (
      state: ScheduledEventState,
      action: PayloadAction<ScheduledEventDeletedEventPayload>
    ) => {
      scheduledEventAdapter.removeOne(state, action.payload.scheduledEventId);
    },
  },
  extraReducers: builder => {
    builder
      .addCase(resetRedux, state => {
        state = { ...initialState };
        return state;
      })
      // Get ScheduledEvent ====================================================
      .addCase(getScheduledEvents.pending, state => {
        state.loadingStatus = 'loading';
      })
      .addCase(getScheduledEvents.fulfilled, (state, action) => {
        scheduledEventAdapter.upsertMany(state, action.payload);
        state.loadingStatus = 'success';
      })
      .addCase(getScheduledEvents.rejected, state => {
        state.loadingStatus = 'failed';
      })

      // Get ScheduledEvent for guide ===========================================
      .addCase(getScheduledEventsForGuide.pending, state => {
        state.loadingGuideEventsStatus = 'loading';
      })
      .addCase(getScheduledEventsForGuide.fulfilled, (state, action) => {
        scheduledEventAdapter.upsertMany(state, action.payload.scheduledEvents);
        state.loadingGuideEventsStatus = 'success';
      })
      .addCase(getScheduledEventsForGuide.rejected, state => {
        state.loadingGuideEventsStatus = 'failed';
      })
      // Create ScheduledEvent =================================================
      .addCase(createScheduledEventSuccess, (state, action) => {
        scheduledEventAdapter.upsertOne(state, action.payload);
        state.loadingStatus = 'success';
      })
      .addCase(updateScheduledEventSuccess, (state, action) => {
        scheduledEventAdapter.upsertOne(state, action.payload);
        state.loadingStatus = 'success';
      })

      // Snooze ScheduledEvent =================================================
      .addCase(snoozeScheduledEventForUser.pending, state => {
        state.loadingStatus = 'loading';
      })
      .addCase(snoozeScheduledEventForUser.fulfilled, (state, action) => {
        scheduledEventAdapter.upsertOne(state, action.payload);
        state.loadingStatus = 'success';
      })
      .addCase(snoozeScheduledEventForUser.rejected, state => {
        state.loadingStatus = 'failed';
      })

      // Delete ScheduledEvent =================================================
      .addCase(deleteScheduledEventForUser.pending, state => {
        state.loadingStatus = 'loading';
      })
      .addCase(deleteScheduledEventForUser.fulfilled, (state, action) => {
        scheduledEventAdapter.removeOne(state, action.payload.id);
        state.loadingStatus = 'success';
      })
      .addCase(deleteScheduledEventForUser.rejected, state => {
        state.loadingStatus = 'failed';
      })

      // Pending Item ScheduledEvent ===========================================
      .addCase(getPendingItems.pending, state => {
        state.loadingStatus = 'loading';
      })
      .addCase(getPendingItems.fulfilled, (state, action) => {
        scheduledEventAdapter.upsertMany(state, action.payload.scheduledEvents);
        state.loadingStatus = 'success';
      })
      .addCase(getPendingItems.rejected, state => {
        state.loadingStatus = 'failed';
      });
  },
});

// REDUCER =====================================================================
export const scheduledEventsReducer = scheduledEventsSlice.reducer;

// SELECTORS ===================================================================
export const selectScheduledEventsState = state =>
  (state as { scheduledEvents: typeof initialState })?.scheduledEvents;

export const {
  selectAll: selectScheduledEvents,
  selectById: selectScheduledEventById,
} = scheduledEventAdapter.getSelectors(selectScheduledEventsState);

export const selectScheduledEventLoadingStatus = createSelector(
  selectScheduledEventsState,
  eventState => eventState.loadingStatus
);

export const selectGuideScheduledEventLoadingStatus = createSelector(
  selectScheduledEventsState,
  eventState => eventState.loadingGuideEventsStatus
);

export interface MemberSelectorParams {
  memberId: string;
  [key: string]: unknown;
}

export const selectScheduledEventsByMember: (
  state,
  paramObj: MemberSelectorParams
) => ScheduledEventDto[] = createSelector(
  [
    selectScheduledEvents,
    (state, paramObj: MemberSelectorParams) => paramObj.memberId,
  ],
  (scheduledEvents, memberId) => {
    return scheduledEvents.filter(s => s.userId === memberId);
  }
);

// EXPORT HELPER ===============================================================
export const useScheduledEventSelectors = {
  useSelectScheduledEventById: (scheduledEventId: number) =>
    useAppSelector(state => selectScheduledEventById(state, scheduledEventId)),
};

// EFFECTS =====================================================================
addToEffectsMiddleware(addEffect => {
  addEffect({
    action: loadScheduledEvents,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(getScheduledEvents({ params: { ...action.payload } }));
      }
    },
  });
  addEffect({
    action: loadScheduledEventsForGuide,
    effect: async ({ dispatch, action }) => {
      if (action.payload) {
        void dispatch(
          getScheduledEventsForGuide({
            params: {
              guideId: action.payload.userId,
              startEpochTime: action.payload.startEpochTime,
              endEpochTime: action.payload.endEpochTime,
              includeMemberDetails: true,
            },
          })
        );
      }
    },
  });
  addEffect({
    action: getScheduledEventsForGuide.fulfilled,
    effect: async ({ dispatch, action }) => {
      void dispatch(newMembersLoaded({ members: action.payload.members }));
    },
  });
});

// WEBSOCKET ACTIONS ===========================================================
export const scheduledEventWebsocketActions: WebsocketEventActions[] = [
  {
    type: ScheduledEventEventTypes.SCHEDULED_EVENT_CREATED,
    action: scheduledEventsSlice.actions.scheduledEventCreated,
    channel: 'baseSocket',
  },
  {
    type: ScheduledEventEventTypes.SCHEDULED_EVENT_UPDATED,
    action: scheduledEventsSlice.actions.scheduledEventUpdated,
    channel: 'baseSocket',
  },
  {
    type: ScheduledEventEventTypes.SCHEDULED_EVENT_DELETED,
    action: scheduledEventsSlice.actions.scheduledEventDeleted,
    channel: 'baseSocket',
  },
];
